// Property Condition — ONE record of the property at handover.
//
// This replaces the two cards that used to sit side by side (room photos and
// a separate item inventory) and confused people into thinking they were
// photographing the same thing twice. They are not:
//
//   Room photos → what the place LOOKED like (walls, floor, paint, seepage)
//   Items       → what was THERE and in what state (motor, tank, sink…)
//
// A wide shot can't prove a motor worked; an item photo can't prove a wall had
// no seepage. A deposit dispute needs both — so both live here, one room at a
// time, instead of in two competing cards.
//
// Storage is unchanged, deliberately: room photos keep going to
// lease.terms.condition_photos (which the move-out comparison already diffs)
// and items to lease.terms.inventory. Nothing migrates; nothing already
// captured is lost.
import React, { useState, useRef, useContext, useMemo } from "react";
import Ctx from "../../contexts/AppContext";
import { uploadPhoto, deletePhoto } from "../../utils/supabaseClient";
import {
    FURNISHING_LEVELS, CONDITIONS, CONDITION_MAP,
    buildInventory, makeItem, leaseInventory,
} from "../../constants/inventoryTemplates";

const MAX_PHOTOS = 20;          // per lease, matches the previous rule
const MAX_ITEM_PHOTOS = 4;

// Room keys match the server's photo categories (server/routes/vacate.js
// REQUIRED_PHOTO_CATEGORIES) so move-in and move-out line up automatically.
const ROOMS = [
    { key: "hall", label: "Hall / Living", icon: "🛋️", required: true },
    { key: "bedroom", label: "Bedroom", icon: "🛏️", required: true },
    { key: "kitchen", label: "Kitchen", icon: "🍳", required: true },
    { key: "bathroom", label: "Bathroom", icon: "🚿", required: true },
    { key: "walls", label: "Walls & Ceiling", icon: "🧱", required: true },
    { key: "meter", label: "Meters", icon: "📊", required: true },
    { key: "utility", label: "Utility / Common", icon: "🔧", required: false },
    { key: "other", label: "Other", icon: "📦", required: false },
];

const parseTerms = (lease) => {
    try { return typeof lease?.terms === "string" ? JSON.parse(lease.terms) : (lease?.terms || {}); }
    catch (_) { return {}; }
};

export default function PropertyCondition({ lease, onSave, editable = true, addedBy = "landlord" }) {
    const { h, flash } = useContext(Ctx);
    const terms = useMemo(() => parseTerms(lease), [lease]);
    const photos = Array.isArray(terms.condition_photos) ? terms.condition_photos : [];
    const inv = useMemo(() => leaseInventory(lease), [lease]);

    const [openCard, setOpenCard] = useState(false);
    const [openRoom, setOpenRoom] = useState(null);
    const [openItem, setOpenItem] = useState(null);
    const [level, setLevel] = useState("semi");
    const [busy, setBusy] = useState(false);
    const [adding, setAdding] = useState(null);
    const [newName, setNewName] = useState("");
    const [lightbox, setLightbox] = useState(null);
    const camRef = useRef(null);
    const galRef = useRef(null);
    const target = useRef(null);   // { roomKey, itemId|null }

    /* ── persistence ─────────────────────────────────────────────────── */
    const persist = async (patch) => {
        const updated = { ...lease, terms: { ...terms, ...patch } };
        await onSave(updated);
    };

    const setInventory = (nextInv) => persist({ inventory: { ...nextInv, updated_at: new Date().toISOString() } });

    const mutateItem = (roomKey, itemId, fn) => setInventory({
        ...inv,
        rooms: inv.rooms.map(r => r.key !== roomKey ? r : { ...r, items: r.items.map(it => it.id !== itemId ? it : fn(it)) }),
    });

    const addItem = (roomKey, name) => {
        const room = ROOMS.find(r => r.key === roomKey);
        const exists = inv.rooms.some(r => r.key === roomKey);
        const rooms = exists
            ? inv.rooms.map(r => r.key !== roomKey ? r : { ...r, items: [...r.items, makeItem(name)] })
            : [...inv.rooms, { key: roomKey, label: room?.label || roomKey, icon: room?.icon || "📦", items: [makeItem(name)] }];
        return setInventory({ ...inv, rooms });
    };

    /* ── photo capture ───────────────────────────────────────────────── */
    const pick = (roomKey, itemId, useCamera) => {
        target.current = { roomKey, itemId };
        const el = useCamera ? camRef.current : galRef.current;
        if (el) { el.value = ""; el.click(); }
    };

    const handleFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        const tgt = target.current;
        if (!files.length || !tgt) return;

        const isItem = !!tgt.itemId;
        let allowed;
        if (isItem) {
            const room = inv?.rooms.find(r => r.key === tgt.roomKey);
            const item = room && room.items.find(i => i.id === tgt.itemId);
            if (!item) return;
            allowed = MAX_ITEM_PHOTOS - (item.photos || []).length;
            if (allowed <= 0) { flash(`Up to ${MAX_ITEM_PHOTOS} photos per item`, "error"); return; }
        } else {
            allowed = MAX_PHOTOS - photos.length;
            if (allowed <= 0) { flash(`Photo limit reached (${MAX_PHOTOS} per agreement)`, "error"); return; }
        }
        const chosen = files.slice(0, allowed);
        if (files.length > chosen.length) flash(`Only ${allowed} more photo${allowed === 1 ? "" : "s"} allowed`);

        setBusy(true);
        try {
            const urls = [];
            for (const f of chosen) urls.push(await uploadPhoto(f, `lease_${lease.id}`, { maxWidth: 1024, quality: 0.75 }));
            const now = new Date().toISOString();
            if (isItem) {
                await mutateItem(tgt.roomKey, tgt.itemId, it => ({
                    ...it, photos: [...(it.photos || []), ...urls.map(u => ({ url: u, added_by: addedBy, created_at: now }))],
                }));
            } else {
                const room = ROOMS.find(r => r.key === tgt.roomKey);
                await persist({
                    condition_photos: [...photos, ...urls.map(u => ({
                        url: u, category: tgt.roomKey, caption: room?.label || tgt.roomKey,
                        notes: "", added_by: addedBy, created_at: now,
                    }))],
                });
            }
            flash(`${urls.length} photo${urls.length === 1 ? "" : "s"} added`);
        } catch (err) {
            flash(err?.message || "Upload failed", "error");
        } finally {
            setBusy(false);
            target.current = null;
        }
    };

    const removeRoomPhoto = async (url) => {
        await persist({ condition_photos: photos.filter(p => p.url !== url) });
        deletePhoto(url).catch(() => {});
    };
    const removeItemPhoto = async (roomKey, itemId, url) => {
        await mutateItem(roomKey, itemId, it => ({ ...it, photos: (it.photos || []).filter(p => p.url !== url) }));
        deletePhoto(url).catch(() => {});
    };

    /* ── derived ─────────────────────────────────────────────────────── */
    const photosFor = (key) => photos.filter(p => (p.category || "").toLowerCase() === key);
    const itemsFor = (key) => (inv?.rooms.find(r => r.key === key)?.items) || [];
    const totals = useMemo(() => {
        let items = 0, shots = 0, flagged = 0;
        for (const r of (inv?.rooms || [])) for (const it of (r.items || [])) {
            items++; shots += (it.photos || []).length;
            if (it.condition === "damaged" || it.condition === "missing") flagged++;
        }
        const missingRooms = ROOMS.filter(r => r.required && photosFor(r.key).length === 0).length;
        return { items, shots, flagged, missingRooms };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inv, photos]);

    const fl = FURNISHING_LEVELS.find(f => f.key === inv?.furnishing);
    const summary = photos.length === 0 && totals.items === 0
        ? "Nothing recorded yet"
        : `${photos.length} room photo${photos.length === 1 ? "" : "s"} · ${totals.items} item${totals.items === 1 ? "" : "s"}` +
          (totals.flagged ? ` · ${totals.flagged} flagged` : "");

    const dimBg = h.dk ? "rgba(255,255,255,0.05)" : "rgba(17,24,39,0.035)";

    return (
        <div style={{ background: h.cd, borderRadius: 20, boxShadow: h.sh, marginBottom: 12, overflow: "hidden" }}>
            <input ref={camRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={handleFiles} style={{ display: "none" }} />
            <input ref={galRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleFiles} style={{ display: "none" }} />

            {/* header */}
            <button onClick={() => setOpenCard(o => !o)} aria-expanded={openCard} style={{
                width: "100%", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit",
                textAlign: "left", padding: "14px 16px", display: "flex", alignItems: "center", gap: 11,
                WebkitTapHighlightColor: "transparent",
            }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{"🏠"}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                        <span style={{ color: h.tx, fontSize: 13.5, fontWeight: 600, letterSpacing: "-0.1px" }}>Property Condition</span>
                        {fl && <span style={{ background: `${h.in || h.ac}12`, color: h.in || h.ac, padding: "2px 8px", borderRadius: 999, fontSize: 9.5, fontWeight: 700 }}>{fl.label}</span>}
                    </span>
                    <span style={{ display: "block", color: h.ts, fontSize: 11, marginTop: 2 }}>{summary}</span>
                </span>
                <span style={{ color: h.tt || h.ts, fontSize: 12, flexShrink: 0, transform: openCard ? "rotate(180deg)" : "none", transition: "transform .22s cubic-bezier(.32,.72,.22,1)" }}>{"▾"}</span>
            </button>

            {openCard && <>
                {/* first run — pick furnishing to seed the item list */}
                {!inv && editable && (
                    <div style={{ padding: "0 16px 14px" }}>
                        <p style={{ color: h.ts, fontSize: 12, lineHeight: 1.55, margin: "0 0 12px" }}>
                            Photograph each room, and list what's inside it — motor, tank, sink, geyser, switches, pipes.
                            At move-out the same record is re-checked, so the deposit settles on evidence.
                        </p>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
                            {FURNISHING_LEVELS.map(f => {
                                const on = level === f.key;
                                return (
                                    <button key={f.key} onClick={() => setLevel(f.key)} style={{
                                        textAlign: "left", cursor: "pointer", fontFamily: "inherit",
                                        background: on ? `${h.ac}0d` : dimBg,
                                        border: `1.5px solid ${on ? h.ac : "transparent"}`, borderRadius: 14,
                                        padding: "11px 13px", display: "flex", alignItems: "center", gap: 11,
                                    }}>
                                        <span style={{
                                            width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                                            border: `2px solid ${on ? h.ac : (h.dk ? "rgba(255,255,255,0.2)" : "rgba(17,24,39,0.16)")}`,
                                            background: on ? h.ac : "transparent",
                                        }} />
                                        <span>
                                            <span style={{ display: "block", color: h.tx, fontSize: 13, fontWeight: 600 }}>{f.label}</span>
                                            <span style={{ display: "block", color: h.ts, fontSize: 11.5, marginTop: 1 }}>{f.hint}</span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        <button disabled={busy} onClick={async () => { setBusy(true); try { await setInventory(buildInventory(level)); flash("Item list created"); } finally { setBusy(false); } }}
                            style={{ width: "100%", padding: "12px 16px", borderRadius: 14, border: "none", background: h.ac, color: "#fff", fontSize: 13.5, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", opacity: busy ? 0.6 : 1 }}>
                            {busy ? "Creating…" : "Start the record"}
                        </button>
                    </div>
                )}

                {/* rooms */}
                {(inv || !editable) && ROOMS.map(room => {
                    const rPhotos = photosFor(room.key);
                    const rItems = itemsFor(room.key);
                    if (!editable && rPhotos.length === 0 && rItems.length === 0) return null;
                    const open = openRoom === room.key;
                    const flagged = rItems.filter(i => i.condition === "damaged" || i.condition === "missing").length;
                    const needsPhoto = room.required && rPhotos.length === 0;
                    return (
                        <div key={room.key} style={{ borderTop: `1px solid ${h.bd}` }}>
                            <button onClick={() => { setOpenRoom(open ? null : room.key); setOpenItem(null); }} style={{
                                width: "100%", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit",
                                display: "flex", alignItems: "center", gap: 11, padding: "12px 16px", textAlign: "left",
                            }}>
                                <span style={{ fontSize: 16, flexShrink: 0 }}>{room.icon}</span>
                                <span style={{ flex: 1, minWidth: 0 }}>
                                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <span style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{room.label}</span>
                                        {needsPhoto && editable && (
                                            <span style={{ background: `${h.wn}14`, color: h.wn, padding: "1px 7px", borderRadius: 999, fontSize: 9, fontWeight: 700 }}>NEEDS PHOTO</span>
                                        )}
                                    </span>
                                    <span style={{ display: "block", color: h.ts, fontSize: 11, marginTop: 1 }}>
                                        {rPhotos.length} photo{rPhotos.length === 1 ? "" : "s"} · {rItems.length} item{rItems.length === 1 ? "" : "s"}
                                        {flagged > 0 && <span style={{ color: h.rd, fontWeight: 600 }}> · {flagged} flagged</span>}
                                    </span>
                                </span>
                                <span style={{ color: h.tt || h.ts, fontSize: 12, flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform .22s" }}>{"▾"}</span>
                            </button>

                            {open && <div style={{ padding: "0 14px 14px" }}>
                                {/* ROOM PHOTOS — the wide shots */}
                                <div style={{ color: h.tt || h.ts, fontSize: 10, fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 7 }}>
                                    Room photos
                                </div>
                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                                    {rPhotos.map(p => (
                                        <div key={p.url} style={{ position: "relative", width: 62, height: 62, borderRadius: 12, overflow: "hidden", background: "#000" }}>
                                            <img src={p.url} alt={room.label} loading="lazy" onClick={() => setLightbox(p.url)}
                                                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", cursor: "pointer" }} />
                                            {editable && <button onClick={() => removeRoomPhoto(p.url)} aria-label="Remove photo" style={{
                                                position: "absolute", top: 2, right: 2, width: 18, height: 18, borderRadius: 9,
                                                background: "rgba(0,0,0,0.55)", border: "none", color: "#fff", fontSize: 10, cursor: "pointer",
                                                display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1,
                                            }}>{"✕"}</button>}
                                        </div>
                                    ))}
                                    {editable && photos.length < MAX_PHOTOS && <>
                                        <button onClick={() => pick(room.key, null, true)} disabled={busy} aria-label="Take room photo"
                                            style={{ width: 62, height: 62, borderRadius: 12, border: `1px dashed ${h.bd}`, background: "none", color: h.ts, fontSize: 18, cursor: "pointer" }}>{"📷"}</button>
                                        <button onClick={() => pick(room.key, null, false)} disabled={busy} aria-label="Choose room photo"
                                            style={{ width: 62, height: 62, borderRadius: 12, border: `1px dashed ${h.bd}`, background: "none", color: h.ts, fontSize: 16, cursor: "pointer" }}>{"🖼"}</button>
                                    </>}
                                    {rPhotos.length === 0 && !editable && (
                                        <span style={{ color: h.ts, fontSize: 11, fontStyle: "italic" }}>No photos</span>
                                    )}
                                </div>

                                {/* ITEMS — what's inside, each with its own state */}
                                <div style={{ color: h.tt || h.ts, fontSize: 10, fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 7 }}>
                                    Items in this room
                                </div>
                                {rItems.length === 0 && (
                                    <div style={{ color: h.ts, fontSize: 11.5, marginBottom: 8 }}>
                                        {editable ? "No items listed for this room yet." : "No items listed."}
                                    </div>
                                )}
                                {rItems.map(item => {
                                    const cond = CONDITION_MAP[item.condition] || CONDITION_MAP.good;
                                    const tone = h[cond.tone] || h.gn;
                                    const expanded = openItem === item.id;
                                    const shots = item.photos || [];
                                    return (
                                        <div key={item.id} style={{ borderRadius: 13, marginBottom: 5, background: expanded ? dimBg : "transparent" }}>
                                            <button onClick={() => setOpenItem(expanded ? null : item.id)} style={{
                                                width: "100%", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit",
                                                display: "flex", alignItems: "center", gap: 9, padding: "8px 10px", textAlign: "left",
                                            }}>
                                                <span style={{ flex: 1, minWidth: 0 }}>
                                                    <span style={{ display: "block", color: h.tx, fontSize: 12.5, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {item.name}{item.qty > 1 ? ` ×${item.qty}` : ""}
                                                    </span>
                                                    {shots.length > 0 && <span style={{ display: "block", color: h.tt || h.ts, fontSize: 10, marginTop: 1 }}>{shots.length} photo{shots.length === 1 ? "" : "s"}</span>}
                                                </span>
                                                <span style={{ background: `${tone}14`, color: tone, padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{cond.label}</span>
                                            </button>

                                            {expanded && <div style={{ padding: "0 10px 11px" }}>
                                                <div style={{ display: "flex", gap: 5, marginBottom: 9 }}>
                                                    {CONDITIONS.map(c => {
                                                        const on = item.condition === c.key;
                                                        const ct = h[c.tone] || h.gn;
                                                        return (
                                                            <button key={c.key} disabled={!editable} title={c.note || c.label}
                                                                onClick={() => editable && mutateItem(room.key, item.id, it => ({ ...it, condition: c.key }))}
                                                                style={{
                                                                    flex: 1, padding: "7px 4px", borderRadius: 10, border: "none", cursor: editable ? "pointer" : "default",
                                                                    background: on ? `${ct}16` : dimBg, color: on ? ct : h.ts,
                                                                    fontSize: 11, fontWeight: on ? 700 : 500, fontFamily: "inherit",
                                                                }}>{c.label}</button>
                                                        );
                                                    })}
                                                </div>

                                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 9 }}>
                                                    {shots.map(p => (
                                                        <div key={p.url} style={{ position: "relative", width: 54, height: 54, borderRadius: 10, overflow: "hidden", background: "#000" }}>
                                                            <img src={p.url} alt={item.name} loading="lazy" onClick={() => setLightbox(p.url)}
                                                                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", cursor: "pointer" }} />
                                                            {editable && <button onClick={() => removeItemPhoto(room.key, item.id, p.url)} aria-label="Remove photo" style={{
                                                                position: "absolute", top: 2, right: 2, width: 17, height: 17, borderRadius: 9,
                                                                background: "rgba(0,0,0,0.55)", border: "none", color: "#fff", fontSize: 9, cursor: "pointer",
                                                                display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1,
                                                            }}>{"✕"}</button>}
                                                        </div>
                                                    ))}
                                                    {editable && shots.length < MAX_ITEM_PHOTOS && <>
                                                        <button onClick={() => pick(room.key, item.id, true)} disabled={busy} aria-label="Take item photo"
                                                            style={{ width: 54, height: 54, borderRadius: 10, border: `1px dashed ${h.bd}`, background: "none", color: h.ts, fontSize: 16, cursor: "pointer" }}>{"📷"}</button>
                                                        <button onClick={() => pick(room.key, item.id, false)} disabled={busy} aria-label="Choose item photo"
                                                            style={{ width: 54, height: 54, borderRadius: 10, border: `1px dashed ${h.bd}`, background: "none", color: h.ts, fontSize: 14, cursor: "pointer" }}>{"🖼"}</button>
                                                    </>}
                                                </div>

                                                {editable ? <>
                                                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                        <span style={{ color: h.ts, fontSize: 11, fontWeight: 600 }}>Qty</span>
                                                        <button onClick={() => mutateItem(room.key, item.id, it => ({ ...it, qty: Math.max(1, (it.qty || 1) - 1) }))} style={qtyBtn(h)}>{"−"}</button>
                                                        <span style={{ color: h.tx, fontSize: 12.5, fontWeight: 700, minWidth: 16, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>{item.qty || 1}</span>
                                                        <button onClick={() => mutateItem(room.key, item.id, it => ({ ...it, qty: Math.min(99, (it.qty || 1) + 1) }))} style={qtyBtn(h)}>{"+"}</button>
                                                        <button onClick={() => setInventory({ ...inv, rooms: inv.rooms.map(r => r.key !== room.key ? r : { ...r, items: r.items.filter(i => i.id !== item.id) }) })}
                                                            style={{ marginLeft: "auto", background: "none", border: "none", color: h.rd, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Remove</button>
                                                    </div>
                                                    <input defaultValue={item.note || ""} maxLength={200}
                                                        onBlur={(e) => { const v = e.target.value; if (v !== (item.note || "")) mutateItem(room.key, item.id, it => ({ ...it, note: v.slice(0, 200) })); }}
                                                        placeholder={item.reading !== undefined ? "Reading (e.g. 4821 units)" : "Note (e.g. small dent on left side)"}
                                                        style={{ width: "100%", marginTop: 8, padding: "9px 11px", borderRadius: 11, border: `1px solid ${h.bd}`, background: h.dk ? "rgba(255,255,255,0.04)" : "#fff", color: h.tx, fontSize: 12, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                                                </> : (item.note ? <div style={{ color: h.ts, fontSize: 11.5, lineHeight: 1.5 }}>{item.note}</div> : null)}
                                            </div>}
                                        </div>
                                    );
                                })}

                                {editable && inv && (adding === room.key ? (
                                    <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                                        <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Item name" maxLength={50}
                                            onKeyDown={(e) => { if (e.key === "Enter") { const n = newName.trim(); if (n) addItem(room.key, n); setNewName(""); setAdding(null); } }}
                                            style={{ flex: 1, padding: "9px 11px", borderRadius: 11, border: `1px solid ${h.bd}`, background: h.dk ? "rgba(255,255,255,0.04)" : "#fff", color: h.tx, fontSize: 12, fontFamily: "inherit", outline: "none" }} />
                                        <button onClick={() => { const n = newName.trim(); if (n) addItem(room.key, n); setNewName(""); setAdding(null); }}
                                            style={{ padding: "0 14px", borderRadius: 11, border: "none", background: h.ac, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Add</button>
                                    </div>
                                ) : (
                                    <button onClick={() => { setAdding(room.key); setNewName(""); }} style={{
                                        width: "100%", marginTop: 2, padding: "9px", borderRadius: 11, border: `1px dashed ${h.bd}`,
                                        background: "none", color: h.ts, fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                                    }}>+ Add item</button>
                                ))}
                            </div>}
                        </div>
                    );
                })}
            </>}

            {/* lightbox */}
            {lightbox && (
                <div onClick={() => setLightbox(null)} style={{
                    position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)", zIndex: 3000,
                    display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
                }}>
                    <img src={lightbox} alt="Condition" style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 12 }} />
                </div>
            )}
        </div>
    );
}

const qtyBtn = (h) => ({
    width: 26, height: 26, borderRadius: 9, border: "none", cursor: "pointer", fontFamily: "inherit",
    background: h.dk ? "rgba(255,255,255,0.07)" : "rgba(17,24,39,0.05)", color: h.tx, fontSize: 14, fontWeight: 700,
    display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1,
});
